/**                                                                       @about
@file: plugin.keyboard-navigation.ts
@desc: Logic for keyboard navigation, built/compiled to ./www
***                                                                           */
import {
  triggerSortEvent,
} from './plugin.common'
import type {
   TriggerSortKeys,
} from './plugin.common'


// @NOTE -> options set in html via globalThis.keyboard_navigation
type GKeyNavOpts = {
  tab_first: boolean;
  tab_override: boolean;
  vim_mode: boolean;
  sork_keys: boolean;
  debug: boolean;
}
const gKeyNavOpts = Object.assign({
  tab_first: true,
  tab_override: true,
  vim_mode: true,
  sork_keys: true,
  debug: false,
}, globalThis._plugin_keyboard_navigation ?? {}) as GKeyNavOpts
const IS_NAV_DEBUG = gKeyNavOpts.debug;


const getTabElements = () => {
  // Get all tabbable elements
  const tabbableElements = Array.from(document.querySelectorAll<HTMLElement>(
    '[tabindex]:not([tabindex="-1"])'
  ));
  // // meh, un-needed overhead
  // .filter(el => {
  //   const style = window.getComputedStyle(el);
  //   return style.display !== 'none' && style.visibility !== 'hidden';
  // });

  // Sort elements by tabIndex
  return tabbableElements.sort((a, b) => {
    const aTabIndex = a.tabIndex || 0;
    const bTabIndex = b.tabIndex || 0;
    if (aTabIndex === bTabIndex) {
      return tabbableElements.indexOf(a) - tabbableElements.indexOf(b);
    }
    return aTabIndex - bTabIndex;
  });
}

const initKeyboardNav = () => {
  IS_NAV_DEBUG && console.log('initKeyboardNav')

  let currentFocus: HTMLElement | null = null;
  let isTabbing = false;
  let isNewSort = false;
  // fired witin triggerSortEvent/plugin.common.ts lets us know the sort needs updatin
  window.addEventListener('quaint.resort', () => {
    isNewSort = true
  });

  // Get all tabbable elements
  let tabbableElements = getTabElements();

  const getTabElement = (id = 'prv', currentElement: HTMLElement | null = currentFocus): HTMLElement | null => {
    // triggered on sort keyboard update
    if (isNewSort) {
      tabbableElements = getTabElements();
    }
    if (!currentElement) {
      IS_NAV_DEBUG && console.log('getTabElements::' + id + ': no forcus elm');
      return null;
    }
    const currentIndex = tabbableElements.indexOf(currentElement);
    if (currentIndex === -1) {
      IS_NAV_DEBUG && console.log('getTabElements::' + id + ': bad index -> ' + String(currentIndex));
      return tabbableElements[0]
    }
    return id === 'prv'
      ? tabbableElements[currentIndex + 1] ?? tabbableElements[0]
      : tabbableElements[currentIndex - 1] ?? tabbableElements[tabbableElements.length - 1];
  };



  const ensureVisibility = (elm = document.activeElement) => {
    if (!elm) { return null; }
    const rect = elm.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const minTopSpace = viewportHeight * 0.25;
    const minBottomSpace = viewportHeight * 0.35;
    const scrollAdjustment = rect.top < minTopSpace
      // top space adj
      ? rect.top - minTopSpace
      : (viewportHeight - rect.bottom < minBottomSpace)
        // bottom space adj
        ? rect.bottom + minBottomSpace - viewportHeight
        : 0;

    // adjust scroll if needed
    if (scrollAdjustment !== 0) {
      IS_NAV_DEBUG && console.log('ensureVisibility:: ' + String(scrollAdjustment));
      window.scrollBy({
        top: scrollAdjustment,
      });
    }
  }


  const handleUp = () => {
    const tabElm = getTabElement('nxt');
    IS_NAV_DEBUG && console.log('handleUp::' + (tabElm ? '1' : '0'));
    tabElm?.focus();
    !tabElm && IS_NAV_DEBUG && console.log('handleUp::fallback?');
    requestAnimationFrame(() => ensureVisibility())
  };

  const handleDown = () => {
    const tabElm = getTabElement('prv');
    IS_NAV_DEBUG && console.log('handleDown::' + (tabElm ? '1' : '0'));
    tabElm?.focus();
    !tabElm && IS_NAV_DEBUG && console.log('handleDown::fallback?');
    requestAnimationFrame(() => ensureVisibility())
  };

  const handleRight = () => {
    IS_NAV_DEBUG && console.log('handleRight');
    if (currentFocus?.getAttribute('data-dnav') === 'back') {
      IS_NAV_DEBUG && console.log('handleRight::skip: cant nav foward');
      handleDown();
      return null;
    }
    // if not dir link/item we click into
    if (currentFocus?.getAttribute('tabindex') === '1') {
      currentFocus.click();
      return null;
    }
    handleDown();
  };

  const handleLeft = () => {
    IS_NAV_DEBUG && console.log('handleLeft', currentFocus?.getAttribute('tabindex'));
    if (currentFocus?.getAttribute('tabindex') === '1') {
      const parentLink = document.querySelector('[href="./index.html"]')
        ?.previousElementSibling?.previousElementSibling as HTMLElement | undefined;
      IS_NAV_DEBUG && console.log('handleDown::' + (parentLink ? '1' : '0'));
      parentLink && parentLink.click();
      return null;
    }
    handleUp();
  };


  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key
    let code = event.keyCode || event.which
    const isShift = event.shiftKey;
    IS_NAV_DEBUG && console.log('onKeyDown::code: ' + String(code) + ' ->  ' + event.key)

    currentFocus = document.activeElement as HTMLElement | null;
    // escape
    if (code === 27) {
      isTabbing = false
      currentFocus?.blur()
      IS_NAV_DEBUG && console.log('onKeyDown::escape: isTabbing=false')
      return null;
    }


    if (code === 9) {
      isTabbing = true
      IS_NAV_DEBUG && console.log('onKeyDown::tab: isTabbing=true')
      // dont override defautl tab action
      if (!gKeyNavOpts.tab_override) { return null; }
      // override code, tab === down, shift+tab === up
      code = isShift ? 38 : 40;
    } else if (isTabbing && !currentFocus) {
      IS_NAV_DEBUG && console.log('onKeyDown::noFocusElm: isTabbing=false')
      isTabbing = false
    }
    // if no tab first, we allways be tabbing
    isTabbing = !gKeyNavOpts.tab_first ? true : isTabbing

    const arrowD = code === 40 || key === 'ArrowDown'
    const arrowU = code === 38 || key === 'ArrowUp'
    const arrowL = code === 37 || key === 'ArrowLeft'
    const arrowR = code === 39 || key === 'ArrowRight'
    const keyh = gKeyNavOpts.vim_mode && (code === 72 || key === 'h');
    const keyj = gKeyNavOpts.vim_mode && (code === 74 || key === 'j');
    const keyk = gKeyNavOpts.vim_mode && (code === 75 || key === 'k');
    const keyl = gKeyNavOpts.vim_mode && (code === 76 || key === 'l');


    // key: h
    if (arrowL || keyh) {
      ((arrowL && isTabbing) || !arrowL) && handleLeft();
      return null;
    }
    // key: j
    if (arrowD || keyj) {
      const isOk = ((arrowD && isTabbing) || !arrowD);
      if (!isOk) { return null; }
      // stop from scrolling on arrow key or key "j" input/action
      if (currentFocus) {
        IS_NAV_DEBUG && console.log('onKeyDown:: event.preventDefault() -> scroll');
        event.stopPropagation();
        event.preventDefault();
      }
      handleDown();
      return null;
    }
    // key: k
    if (arrowU || keyk) {
      const isOk = ((arrowU && isTabbing) || !arrowU);
      if (!isOk) { return null; }
      // stop from scrolling on arrow key or key "k" input/action
      if (currentFocus) {
        IS_NAV_DEBUG && console.log('onKeyUp:: event.preventDefault() -> scroll');
        event.stopPropagation();
        event.preventDefault();
      }
      handleUp();
      return null;
    }
    // key: l
    if (arrowR || keyl) {
      ((arrowR && isTabbing) || !arrowR) && handleRight();
      return null;
    }

    // skip sork_keys logic, disabled
    if (!gKeyNavOpts.sork_keys) { return null; }

    const triggerEvent = (key: TriggerSortKeys, eventType = 'click') => {
      const prvFocusElm = triggerSortEvent(key, eventType)
      if (!prvFocusElm) { return null; }
      isNewSort = true
      // since we will lose focus, stash-it, and re-focus
      requestAnimationFrame(() => {
        prvFocusElm.focus()
        ensureVisibility(prvFocusElm);
      })
    }

    if (code === 83 || key === 's') {
      IS_NAV_DEBUG && console.log('onKeyDown::sort: size');
      triggerEvent('size');
      return null;
    }
    if (code === 70 || key === 'f') {
      IS_NAV_DEBUG && console.log('onKeyDown::sort: filename');
      triggerEvent('filename');
      return null;
    }
    if (code === 69 || key === 'e') {
      IS_NAV_DEBUG && console.log('onKeyDown::sort: extension');
      triggerEvent('extension');
      return null;
    }
    if (code === 84 || key === 't') {
      IS_NAV_DEBUG && console.log('onKeyDown::sort: time');
      triggerEvent('time');
      return null;
    }
  }


  // unfortunaly we have to hijack key down and prevent defualt arrow key scroll
  // since scroll-snap, is piss-poor for this type of thing, and relying on 
  // browser to set position based on focus is a lost cause due to up/down arrow
  // key conflict with scroll and focus of elemnt
  document.addEventListener('keydown', onKeyDown, {
    // ensures we intercept scroll/down event
    capture: true,
    // since we trigger preventDefault
    passive: false
  });
}


// Called when the page loads
document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initKeyboardNav)
  : initKeyboardNav()

